import { useCallback, useEffect, useState } from 'react';
import { Sparkles, UserPlus, Users } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Getting teams into existence, above the teams themselves.
 *
 * Team Management could review, approve, rename and assign — everything except
 * make a team. So an organiser forming teams from a registrant list had nowhere
 * to start, and with sixty people, doing it four at a time by hand is an
 * afternoon.
 */

interface HackathonOption { _id: string; title: string }
interface Person { userId: string | null; name: string; email: string; hasAccount: boolean; inTeam: string | null; skills: string[]; lookingForTeam: boolean }
interface PlannedTeam { name: string; leader: string; members: string[] }

export default function TeamFormingBar({ onChanged }: { onChanged?: () => void }) {
  const { success, error } = useToast();
  const [hackathons, setHackathons] = useState<HackathonOption[]>([]);
  const [hackathonId, setHackathonId] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [counts, setCounts] = useState({ total: 0, withoutAccount: 0, available: 0 });
  const [open, setOpen] = useState<'none' | 'manual' | 'auto'>('none');
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [leaderId, setLeaderId] = useState('');

  const [teamSize, setTeamSize] = useState(4);
  const [plan, setPlan] = useState<PlannedTeam[] | null>(null);

  useEffect(() => {
    api.get('/hackathons')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data.hackathons || []);
        setHackathons(list.map((h: any) => ({ _id: h._id, title: h.title })));
        if (list.length === 1) setHackathonId(list[0]._id);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) { setPeople([]); setCounts({ total: 0, withoutAccount: 0, available: 0 }); return; }
    try {
      const res = await api.get(`/teams/admin/available/${id}`);
      setPeople(res.data.people || []);
      setCounts({ total: res.data.total, withoutAccount: res.data.withoutAccount, available: res.data.available });
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not read the participants');
    }
  }, [error]);

  useEffect(() => { load(hackathonId); setPicked([]); setLeaderId(''); setPlan(null); }, [hackathonId, load]);

  const free = people.filter((p) => p.hasAccount && !p.inTeam);

  const createManual = async () => {
    setBusy(true);
    try {
      const res = await api.post('/teams/admin/create', {
        hackathonId, name, leaderId, memberIds: picked.filter((id) => id !== leaderId)
      });
      success(`${res.data.team.name} created — join code ${res.data.team.joinCode}`);
      setName(''); setPicked([]); setLeaderId(''); setOpen('none');
      load(hackathonId); onChanged?.();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not create the team');
    } finally { setBusy(false); }
  };

  const previewAuto = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/teams/admin/auto-form/${hackathonId}`, { teamSize, preview: true });
      setPlan(res.data.plan || []);
    } catch (err: any) {
      setPlan(null);
      error(err.response?.data?.message || 'Could not work out a plan');
    } finally { setBusy(false); }
  };

  const runAuto = async () => {
    if (!plan) return;
    if (!window.confirm(
      `Create ${plan.length} team${plan.length === 1 ? '' : 's'} from the people waiting?\n\n` +
      'Everyone already in a team is left alone. You can rename, move people, or delete any of them afterwards.'
    )) return;

    setBusy(true);
    try {
      const res = await api.post(`/teams/admin/auto-form/${hackathonId}`, { teamSize });
      success(res.data.message);
      setPlan(null); setOpen('none');
      load(hackathonId); onChanged?.();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not form the teams');
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px]">
          <label htmlFor="tf-hackathon" className="mb-1 block text-sm font-medium text-gray-700">Hackathon</label>
          <select
            id="tf-hackathon" value={hackathonId} onChange={(e) => setHackathonId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2"
          >
            <option value="">Choose a hackathon…</option>
            {hackathons.map((h) => <option key={h._id} value={h._id}>{h.title}</option>)}
          </select>
        </div>

        {hackathonId && (
          <>
            <button type="button" onClick={() => setOpen(open === 'manual' ? 'none' : 'manual')}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <UserPlus className="h-4 w-4" /> Form a team
            </button>
            <button type="button" onClick={() => { setOpen(open === 'auto' ? 'none' : 'auto'); setPlan(null); }}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
              <Sparkles className="h-4 w-4" /> Form teams for everyone waiting
            </button>
          </>
        )}
      </div>

      {hackathonId && (
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <span className="text-gray-600"><Users className="mr-1 inline h-4 w-4" />{counts.total} taking part</span>
          <span className="text-green-700">{counts.available} waiting to be placed</span>
          {counts.withoutAccount > 0 && (
            <span className="text-amber-800">{counts.withoutAccount} cannot be placed — no account yet</span>
          )}
        </div>
      )}

      {open === 'manual' && hackathonId && (
        <div className="mt-4 rounded-md border border-gray-200 p-4">
          <label htmlFor="tf-name" className="mb-1 block text-sm font-medium text-gray-700">Team name</label>
          <input id="tf-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Team Aakash"
            className="mb-3 w-full max-w-sm rounded-md border border-gray-300 px-3 py-2" />

          {free.length === 0 ? (
            <p className="text-sm text-gray-600">Nobody is waiting to be placed.</p>
          ) : (
            <ul className="max-h-64 space-y-1 overflow-y-auto rounded border border-gray-200 p-2">
              {free.map((p) => (
                <li key={p.userId!} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-gray-50">
                  <input type="checkbox" checked={picked.includes(p.userId!)}
                    onChange={() => {
                      setPicked((prev) => prev.includes(p.userId!) ? prev.filter((x) => x !== p.userId) : [...prev, p.userId!]);
                      if (leaderId === p.userId) setLeaderId('');
                    }} className="h-4 w-4" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-900">{p.name || p.email}</p>
                    <p className="truncate text-xs text-gray-500">
                      {p.email}{p.skills.length > 0 && <> · {p.skills.slice(0, 3).join(', ')}</>}
                    </p>
                  </div>
                  {picked.includes(p.userId!) && (
                    <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-700">
                      <input type="radio" name="tf-leader" checked={leaderId === p.userId}
                        onChange={() => setLeaderId(p.userId!)} className="h-3.5 w-3.5" /> leader
                    </label>
                  )}
                </li>
              ))}
            </ul>
          )}

          <button type="button" onClick={createManual} disabled={busy || !name.trim() || !leaderId}
            className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Creating…' : `Create the team${picked.length ? ` with ${picked.length}` : ''}`}
          </button>
          {picked.length > 0 && !leaderId && (
            <p className="mt-2 text-sm text-amber-800">Choose which of them leads.</p>
          )}
        </div>
      )}

      {open === 'auto' && hackathonId && (
        <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="tf-size" className="mb-1 block text-sm font-medium text-gray-700">People per team</label>
              <input id="tf-size" type="number" min={1} max={10} value={teamSize}
                onChange={(e) => { setTeamSize(Number(e.target.value)); setPlan(null); }}
                className="w-24 rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <button type="button" onClick={previewAuto} disabled={busy}
              className="rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50">
              {busy ? 'Working…' : 'Show me the plan'}
            </button>
          </div>

          {plan && (
            <div className="mt-4">
              <p className="mb-2 text-sm text-blue-900">
                <strong>{plan.length}</strong> team{plan.length === 1 ? '' : 's'} would be created. Nobody already
                in a team is touched.
              </p>
              <ul className="max-h-64 space-y-1 overflow-y-auto rounded border border-blue-200 bg-white p-2 text-sm">
                {plan.map((t) => (
                  <li key={t.name} className="px-2 py-1.5">
                    <span className="font-medium text-gray-900">{t.name}</span>
                    <span className="text-gray-600"> — {t.members.length} people, led by {t.leader}</span>
                    <span className="block truncate text-xs text-gray-500">{t.members.join(', ')}</span>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={runAuto} disabled={busy}
                className="mt-3 rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Creating…' : `Create these ${plan.length}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
